import { Box, TextField, FormHelperText, Grid, InputAdornment, Typography } from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Base_Unit_Type_Enum, Billing_Type_Enum, Measurement_Unit_Enum } from '@kheops/graphql';
import { ComputePackagingPriceFromVolumePrice, FormatLogisticalPackaging, GetConvertedUnit, round } from '@kheops/utils';
import { Controller, useFormContext } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import ValidatorsUtils from '../../../common/utils/validators.utils';
import { ProductPackagingsFormData } from '../ProductFormPackagings';
import { arePricePrerequisitesNotFilled } from '../utils';
import { draftProductAtom } from '../state';

import { packagingDefaultValue } from '../../hooks/usePackagingsFormDefaultValues';

interface PackagingPriceProps {
  baseUnitIndex: number;
  packagingIndex: number;
}

export default function PackagingPrice({ baseUnitIndex, packagingIndex }: PackagingPriceProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'packaging', 'products', 'price', 'validation']);
  const { control, formState, setValue, watch } = useFormContext<ProductPackagingsFormData>();
  const draftProduct = useAtomValue(draftProductAtom);
  const [baseUnit, packaging, baseUnitPrice] = watch([
    `productPackagings.${baseUnitIndex}.csu`,
    `productPackagings.${baseUnitIndex}.packagings.${packagingIndex}`,
    `productPackagings.${baseUnitIndex}.packagings.${packagingIndex}.base_unit_price`,
  ]);

  const pricePrerequisitesNotFilled = arePricePrerequisitesNotFilled(baseUnit, packaging);
  const packagingPriceNotReady = pricePrerequisitesNotFilled || !baseUnitPrice;
  const [baseUnitPriceInput, setBaseUnitPriceInput] = useState<string | undefined>(() => baseUnitPrice.toString());

  const isBilledByWeight = useMemo((): boolean => {
    return baseUnit.billing_type === Billing_Type_Enum.WeightDependent;
  }, [baseUnit.billing_type]);

  const isBulk = useMemo((): boolean => {
    return baseUnit.csu_type === 'BULK';
  }, [baseUnit.csu_type]);

  const volumeUnits = useMemo(() => {
    if (pricePrerequisitesNotFilled) {
      return;
    }

    return GetConvertedUnit((isBulk ? packaging.content_measurement_unit : baseUnit.content_measurement_unit)!);
  }, [formState, pricePrerequisitesNotFilled, isBulk]);

  const priceMeasurementUnit = useMemo((): string | undefined => {
    if ((isBilledByWeight || isBulk) && volumeUnits) {
      return volumeUnits.volumeUnit === Measurement_Unit_Enum.L ? Measurement_Unit_Enum.L : volumeUnits.volumeUnit.toLowerCase();
    }
  }, [isBilledByWeight, isBulk, volumeUnits]);

  const formattedPackagingTitle = useMemo((): string => {
    let title = `${t('common:packaging_one')} ${packagingIndex + 1}`;

    if (!pricePrerequisitesNotFilled) {
      title += ` : ${FormatLogisticalPackaging(packaging, 'fr', isBulk ? { unit_type: Base_Unit_Type_Enum.Bulk, billing_type: baseUnit.billing_type } : undefined)}`;
    }

    return title;
  }, [formState, pricePrerequisitesNotFilled]);

  useEffect((): void => {
    if (pricePrerequisitesNotFilled) {
      return;
    }

    let newPackagingPrice = packaging.base_unit_price * (packaging.unit_quantity || packaging.net_content)!;

    if (isBilledByWeight || isBulk) {
      newPackagingPrice = ComputePackagingPriceFromVolumePrice(
        packaging,
        {
          ...baseUnit,
          unit_type: isBulk ? Base_Unit_Type_Enum.Bulk : baseUnit.unit_type,
        },
        packaging.base_unit_price,
      );
    }

    newPackagingPrice = round(newPackagingPrice, 3);

    if (newPackagingPrice !== packaging.price) {
      setValue(`productPackagings.${baseUnitIndex}.packagings.${packagingIndex}.price`, newPackagingPrice, { shouldDirty: true });
      setValue(`productPackagings.${baseUnitIndex}.packagings.${packagingIndex}.base_unit_price`, packaging.base_unit_price);
    }
  }, [isBilledByWeight, formState, pricePrerequisitesNotFilled, packaging.base_unit_price]);

  const priceFieldTitle = useMemo(() => {
    if (priceMeasurementUnit) {
      if (priceMeasurementUnit.toUpperCase() === Measurement_Unit_Enum.Unit) {
        return t('packaging:per_unit');
      }

      return t('packaging:per_measurement_unit', { measurementUnit: priceMeasurementUnit });
    }

    return t('products:base_unit_one');
  }, [priceMeasurementUnit]);

  const maxPrice = useMemo(() => {
    if (baseUnit.suggested_retail_price) {
      return round(baseUnit.suggested_retail_price / (1 + draftProduct!.vat_rate), 2);
    }

    return Infinity;
  }, [packaging, baseUnit.suggested_retail_price]);

  return (
    <Box>
      <Typography variant="labelLarge">
        {formattedPackagingTitle}
      </Typography>
      <Grid container sx={{ mt: 2 }} columnSpacing={2}>
        <Grid size={{ xs: 6 }}>
          <Typography variant="bodySmall" sx={{ mb: 0.5 }}>
            {priceFieldTitle}*
          </Typography>
          <Controller
            name={`productPackagings.${baseUnitIndex}.packagings.${packagingIndex}.base_unit_price`}
            control={control}
            rules={{
              required: t('validation:this_field_is_required') as string,
              max: ({ value: maxPrice, message: t('validation:price_superior_to_srp') }),
              validate: {
                pattern: (value) =>
                  ValidatorsUtils.validatePrice(value as number, 3) || t('validation:this_value_should_contain_only_n_decimals', { value: 3 }) as string,
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <TextField
                  name={field.name}
                  value={baseUnitPriceInput}
                  onChange={(event) => {
                    const rawValue = event.target.value;
                    const parsedValue = parseFloat(rawValue.replace(',', '.'));

                    setBaseUnitPriceInput(event.target.value);

                    if (!Number.isNaN(parsedValue)) {
                      field.onChange(parsedValue);
                    } else {
                      field.onChange(undefined)
                    }
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          {priceMeasurementUnit
                            ? `€/${t(`packaging:${priceMeasurementUnit}`, { count: 1 })}`
                            : <EuroIcon />}
                        </InputAdornment>
                      ),
                    },
                    htmlInput: {
                      step: '.01',
                      min: '0',
                    },
                  }}
                  error={!!fieldState.error}
                  fullWidth
                  variant="outlined"
                  type="number"
                  placeholder={t('price:price_placeholder')}
                />
                {fieldState.error && <FormHelperText sx={{ ml: 2 }} error>{fieldState.error?.message}</FormHelperText>}
              </>
            )}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant="bodySmall" sx={{ mb: 0.5 }}>{t('common:packaging_one')}*</Typography>
          <TextField
            disabled
            value={packagingPriceNotReady ? packagingDefaultValue.price : packaging.price}
            fullWidth
            variant="outlined"
            placeholder={t('products:auto_compute_field')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <EuroIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
